/**
 * The last settings the server sent, kept on the device.
 *
 * Home decides whether to offer "Order a Tiffin" from them. Fetched afresh on
 * every start, they arrived only when the server answered — seconds, when it
 * was waking up — and the button appeared late and pushed the page about.
 * Seeded from here while the splash is up, Home opens with what it had last
 * time, and the fresh copy replaces it in the background.
 */

import type { AppConfig } from '@mfa/domain';

import { storage } from '../storage/storage';

const KEY = 'mfa.config.v1';

/** Pakistan keeps no daylight saving: the kitchen is UTC+5 all year. */
const KITCHEN_OFFSET_MS = 5 * 3_600_000;

/** Today on the kitchen's clock, by this device's. */
function kitchenToday(timezone: string | undefined): string {
  try {
    // en-CA writes dates as YYYY-MM-DD.
    return new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(new Date());
  } catch {
    return new Date(Date.now() + KITCHEN_OFFSET_MS).toISOString().slice(0, 10);
  }
}

export async function rememberConfig(config: AppConfig): Promise<void> {
  try {
    await storage.setItem(KEY, JSON.stringify(config));
  } catch {
    // Remembering is a convenience; the fetched copy is already in use.
  }
}

export async function rememberedConfig(): Promise<AppConfig | null> {
  try {
    const raw = await storage.getItem(KEY);
    if (!raw) return null;
    const config = JSON.parse(raw) as AppConfig;
    // The kitchen's date has moved on since it was stored; the device's clock
    // is the better guess until the server answers.
    if (config.tiffin) {
      config.tiffin = { ...config.tiffin, today: kitchenToday(config.tiffin.timezone) };
    }
    return config;
  } catch {
    return null;
  }
}
